// ============================================================================
// BOQ — Schematic-Design area-rate corpus (boq.sd_rates)
// ============================================================================
// SD estimates are priced by subtrade as a rate per sqm of a building-area
// basis. boq.sd_rates is the cross-project corpus of those rates, keyed to the
// brio_trade subtrade classification_nodes (subtrade_node_id). This hook reads
// the corpus and supports manual add / delete (admin/supervisor via RLS).
//
// Escalation + aggregation are NOT done here — the page composes this with
// useBoqCostIndex + utils/sdRateStats, mirroring how the DD/TD rate library
// (useBoqRateLibrary) feeds escalation. All reads/writes use raw PostgREST on
// the boq schema, same as the rest of the BOQ app.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { formatSupabaseError } from '../../hooks/useSupabase';

const num = (v) => (v === null || v === undefined ? null : Number(v));

const SELECT = `
  id, subtrade_node_id, measure_basis, rate, currency, region, rate_date,
  project_id, source_bill_version_id, city, source, note, created_at,
  typology_id, source_zone_id, zone_label,
  projects:project_id ( project_code, name ),
  typology:typology_id ( id, label )
`.replace(/\s+/g, ' ');

const mapRate = (r) => ({
  id: r.id,
  subtradeNodeId: r.subtrade_node_id,
  measureBasis: r.measure_basis,
  rate: num(r.rate),
  currency: r.currency || 'INR',
  region: r.region || 'IN',
  rateDate: r.rate_date,
  projectId: r.project_id,
  projectCode: r.projects?.project_code || null,
  projectName: r.projects?.name || null,
  sourceBillVersionId: r.source_bill_version_id,
  city: r.city,
  source: r.source,
  note: r.note,
  createdAt: r.created_at,
  typologyId: r.typology_id,
  typologyLabel: r.typology?.label || null,
  sourceZoneId: r.source_zone_id,
  zoneLabel: r.zone_label,
});

export const useBoqSdRates = ({ enabled = true } = {}) => {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fErr } = await supabase
        .schema('boq')
        .from('sd_rates')
        .select(SELECT)
        .order('rate_date', { ascending: false });
      if (fErr) throw fErr;
      setRates((data || []).map(mapRate));
    } catch (err) {
      console.error('[useBoqSdRates] fetch failed', err);
      setError(formatSupabaseError(err, { action: 'fetch', entity: 'SD rates' }));
    } finally {
      setLoading(false);
    }
  }, []);

  // Bulk-insert manual SD rates. RLS restricts writes to admin/supervisor.
  const createRates = useCallback(async (rows) => {
    if (!rows?.length) return { success: true, count: 0 };
    try {
      const { data: sessionData, error: sErr } = await supabase.auth.getSession();
      if (sErr || !sessionData?.session) return { success: false, error: 'Please sign in.' };
      const payload = rows.map((row) => ({
        subtrade_node_id: row.subtradeNodeId,
        measure_basis: row.measureBasis,
        rate: Number(row.rate),
        currency: row.currency || 'INR',
        region: row.region || 'IN',
        rate_date: row.rateDate || new Date().toISOString().slice(0, 10),
        project_id: row.projectId || null,
        city: row.city?.trim() || null,
        source: row.source || 'manual',
        note: row.note?.trim() || null,
        typology_id: row.typologyId || null,
        zone_label: row.zoneLabel?.trim() || null,
      }));
      const { data, error: insErr } = await supabase
        .schema('boq').from('sd_rates').insert(payload).select(SELECT);
      if (insErr) throw insErr;
      const mapped = (data || []).map(mapRate);
      setRates((prev) => [...mapped, ...prev]);
      return { success: true, count: mapped.length };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'SD rates' }) };
    }
  }, []);

  const deleteMany = useCallback(async (ids) => {
    if (!ids?.length) return { success: true };
    try {
      const { error: dErr } = await supabase.schema('boq').from('sd_rates').delete().in('id', ids);
      if (dErr) throw dErr;
      const idSet = new Set(ids);
      setRates((prev) => prev.filter((r) => !idSet.has(r.id)));
      return { success: true };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'SD rates' }) };
    }
  }, []);

  useEffect(() => { if (enabled) fetchAll(); }, [enabled, fetchAll]);

  return { rates, loading, error, refresh: fetchAll, createRates, deleteMany };
};

export default useBoqSdRates;
